
import React, { useEffect } from 'react';
import { FormattedHTMLMessage, injectIntl, InjectedIntl } from 'react-intl';
import { produce } from 'immer';
import {
    VerificationService,
    TeacherPersonalInfoViewModel,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    TeacherPersonalInfoResponse,
    hasFailedInstantMatch,
    FormSelectChoice,
    Country,
    getAvailableCountryChoices,
    getAvailableLocaleChoices,
    getDefaultCountryChoice,
    LogoComponent,
    ChangeLocaleComponent,
    handleCountryOnKeyDown,
    setFocus,
    TeacherSchoolComponent,
    Organization,
    updateViewModelOrganization,
    updateFieldValidationErrorsByFieldId,
    handleEmailOnKeyDown,
    shouldCollectPostalCode,
    submitForm,
    VerificationStepsEnum,
    isFormFilled,
    setRef,
    EmailComponent,
    PhoneNumberComponent,
    HowDoesVerifyingWorkComponent,
    FirstNameComponent,
    FormFooterComponent,
    LastNameComponent,
    InputSelectOnKeyDown,
    CountryComponent,
    Locale,
    getSafe,
    PoweredByComponent,
    State,
} from '@sheerid/jslib';
import { PostalCodeComponent } from '../FormFields/PostalCode/PostalCodeComponent';
import { AreaOfStudyComponent } from '../FormFields/AreaOfStudy/AreaOfStudyComponent';
import { PlanOfUseComponent } from '../FormFields/PlanOfUse/PlanOfUseComponent';
import { programId } from '../../programOptions';
import { SchoolTypeComponent } from '../FormFields/SchoolType/SchoolTypeComponent';
import { RegionSelectComponent } from '../FormFields/RegionSelectComponent';

interface StepTeacherPersonalInfoComponentProps {
    intl: InjectedIntl;
    verificationService: VerificationService;
}

const StepTeacherPersonalInfo = ({ intl, verificationService }: StepTeacherPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as TeacherPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as TeacherPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const countryChoices: FormSelectChoice<Country, string>[] = getAvailableCountryChoices(verificationService.programTheme, intl);
    const localeChoices: FormSelectChoice<Locale, string>[] = getAvailableLocaleChoices(verificationService.programTheme, intl);
    const defaultCountryChoice: FormSelectChoice<Country, string> = getDefaultCountryChoice(countryChoices);
    const defaultLocaleChoice: FormSelectChoice<Locale, string> = { value: 'en-US', label: 'English' };

    const updateTeacherViewModel = (key: keyof TeacherPersonalInfoViewModel, value: any) => {
        const nextState: TeacherPersonalInfoViewModel = produce(viewModel, (draft: TeacherPersonalInfoViewModel) => {
            (draft[key] as any) = value;
            if (draft.countryChoice) {
                draft.metadata = {
                    ...draft.metadata,
                    country: draft.countryChoice.value,
                };
            }
        });
        verificationService.updateViewModel(nextState);
    };

    const getAvailableSchoolTypes = () => {
        const availableStatuses: FormSelectChoice[] = [
            { value: 'elementarySchool', label: intl.formatHTMLMessage({ id: 'elementarySchool', defaultMessage: 'Elementary School' }) },
            { value: 'middleSchool', label: intl.formatHTMLMessage({ id: 'middleSchool', defaultMessage: 'Middle School' }) },
        ];

        return availableStatuses;
    };
    const schoolTypes = getAvailableSchoolTypes();

    const handleSchoolTypesOnKeyDown: InputSelectOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            setFocus('organization');
        }
    };

    const updateLocale = (localeChoice: FormSelectChoice<Locale, string>) => {
        const nextState: TeacherPersonalInfoViewModel = produce(viewModel, (draft: TeacherPersonalInfoViewModel) => {
            draft.localeChoice = localeChoice;
        });
        verificationService.updateLocale(
            nextState,
            verificationService.programTheme,
            verificationService.verificationResponse.segment,
        );
    };

    const updateCountryChoice = (countryChoice: FormSelectChoice<Country, string>) => {
        const nextState: TeacherPersonalInfoViewModel = produce(viewModel, (draft: TeacherPersonalInfoViewModel) => {
            draft.countryChoice = countryChoice;

            const country = countryChoice ? countryChoice.value : undefined;

            if (viewModel.countryChoice?.value !== country) {
                draft.metadata = {
                    ...viewModel.metadata,
                    region: '',
                };
            }
        });
        verificationService.updateViewModel(nextState);
    };

    React.useEffect(() => {
        updateCountryChoice(defaultCountryChoice);
    }, []);

    // Don't validate DOB if ID Check is not enabled
    useEffect(() => {
        if (!verificationService.programTheme.idCheckAddOnEnabled) {
            updateTeacherViewModel('birthDate', '');
            updateTeacherViewModel('fieldsToSkipValidation', ['birthDate']);
        } else {
            updateTeacherViewModel('fieldsToSkipValidation', []);
        }
    }, [verificationService.programTheme.idCheckAddOnEnabled]);

    return (
        <div id="sid-step-teacher-personal-info" className="sid-l-container">


            {
                failedInstantMatch
                    ? (
                        <div className="sid-header">
                            <div className="sid-l-horz-center">
                                <LogoComponent verificationService={verificationService} />
                            </div>
                            <div className="sid-header__title sid-l-horz-center">
                                <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Email" />
                            </div>
                            <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                                <FormattedHTMLMessage
                                    id="step.personalInfo.tryAgain.subtitle"
                                    defaultMessage="Your full name and email address must match what is on record with your school."
                                />
                            </div>
                        </div>
                    )
                    : (
                        <div className="sid-header">
                            <div className="sid-l-horz-center">
                                <LogoComponent verificationService={verificationService} />
                            </div>
                            <div className="sid-header__title sid-l-horz-center">
                                <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Teaching Staff-Only Offer" />
                            </div>
                            <div className="sid-header__subtitle sid-l-horz-center">
                                <FormattedHTMLMessage
                                    id="step.personalInfo.subtitle"
                                    defaultMessage="Verify you're a teacher, faculty or staff at a K-12 or degree-granting university."
                                />
                                &nbsp;
                                <HowDoesVerifyingWorkComponent verificationService={verificationService} />
                            </div>
                        </div>
                    )
            }

            {
                (localeChoices.length > 1)
                    ? (
                        <ChangeLocaleComponent
                            options={localeChoices}
                            value={viewModel.localeChoice || defaultLocaleChoice}
                            isErrored={false}
                            onChange={(localeChoice: FormSelectChoice<Locale, string>) => updateLocale(localeChoice)}
                        />
                    ) : null
            }

            {
                (countryChoices.length > 1)
                    ? (
                        <CountryComponent
                            options={countryChoices}
                            value={viewModel.countryChoice}
                            isErrored={false}
                            onKeyDown={event => handleCountryOnKeyDown(event)}
                            placeholder={
                                intl.formatHTMLMessage({
                                    id: 'countryPlaceholder',
                                    defaultMessage: 'Country',
                                })
                            }
                            onChange={(countryChoice: FormSelectChoice<Country, string>) => {
                                updateCountryChoice(countryChoice);
                                if (countryChoice && countryChoice.value) {
                                    setFocus('school-type');
                                }
                            }}
                        />
                    ) : null
            }

            <RegionSelectComponent
                verificationService={verificationService}
                country={viewModel.countryChoice?.value}
                value={viewModel.metadata?.region}
                onChange={
                    (newValue: FormSelectChoice<State, string>) => {
                        updateTeacherViewModel('metadata', {
                            ...verificationService.viewModel.metadata,
                            region: newValue?.value.toString() || '',
                        });
                        updateFieldValidationErrorsByFieldId('region', newValue?.value, verificationService);
                    }
                }
                isRequired
                isErrored={!!fieldValidationErrors.region}
            />

            <div className="sid-field__label sid-additional-info">
                <FormattedHTMLMessage id="onlySupport" defaultMessage="At this time we can only support middle or elementary school educators in the US, UK, Australia and Canada." />
            </div>

            <SchoolTypeComponent
                value={
                    (viewModel.metadata && viewModel.metadata['school-type'])
                        ? ({
                            value: viewModel.metadata['school-type'] as string,
                            label: intl.formatHTMLMessage({
                                id: viewModel.metadata['school-type'] as string,
                                defaultMessage: viewModel.metadata['school-type'] as string,
                            }),
                        }) : null
                }
                isErrored={!!fieldValidationErrors['school-type']}
                options={schoolTypes}
                intl={intl}
                onChange={
                    (status: FormSelectChoice) => {
                        updateTeacherViewModel('metadata', { ...verificationService.viewModel.metadata, 'school-type': status ? (status.value as string) : '' });
                        updateFieldValidationErrorsByFieldId('school-type', status ? (status.value as string) : '', verificationService);
                        if (status) {
                            setFocus('organization');
                        }
                    }
                }
                placeholder={
                    intl.formatHTMLMessage({
                        id: 'schoolType',
                        defaultMessage: 'Type of School',
                    })
                }
                onKeyDown={event => handleSchoolTypesOnKeyDown(event)}
            />

            <TeacherSchoolComponent
                value={(verificationService.viewModel as TeacherPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                placeholder={
                    intl.formatHTMLMessage({
                        id: 'school',
                        defaultMessage: 'Name of College/University',
                    })
                }
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <FirstNameComponent
                value={viewModel.firstName}
                isErrored={!!fieldValidationErrors.firstName}
                placeholder={
                    intl.formatHTMLMessage({
                        id: 'firstName',
                        defaultMessage: 'Legal First Name',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }
                }
            />

            <LastNameComponent
                value={viewModel.lastName}
                isErrored={!!fieldValidationErrors.lastName}
                placeholder={
                    intl.formatHTMLMessage({
                        id: 'lastName',
                        defaultMessage: 'Legal Last Name',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }
                }
            />

            <EmailComponent
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={(
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Must be your school-issued email address"
                    />
                )}
                placeholder={
                    intl.formatHTMLMessage({
                        id: 'email',
                        defaultMessage: 'School - Issued Email Address',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            {
                (verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled)
                    ? (
                        <PhoneNumberComponent
                            isRequired={!!verificationService.programTheme.smsLoopEnabled}
                            value={viewModel.phoneNumber}
                            isErrored={!!fieldValidationErrors.phoneNumber}
                            internationalSupport
                            onChange={
                                (newValue: string) => {
                                    updateTeacherViewModel('phoneNumber', newValue);
                                    updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                                }
                            }
                        />
                    ) : null
            }

            {
                shouldCollectPostalCode(verificationService.programTheme, viewModel)
                    ? (
                        <PostalCodeComponent
                            isErrored={!!fieldValidationErrors.postalCode}
                            onChange={(newValue: string) => {
                                updateTeacherViewModel('postalCode', newValue);
                                updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                            }}
                            value={viewModel.postalCode}
                            placeholder={
                                intl.formatHTMLMessage({
                                    id: 'postalCodePlaceholder',
                                    defaultMessage: 'ZIP Code or Postal Code',
                                })
                            }
                        />
                    ) : null
            }

            <div className="sid-field__label sid-additional-info">
                <FormattedHTMLMessage id="subjectArea" defaultMessage="Subject Area" />
            </div>

            <AreaOfStudyComponent
                value={getSafe(() => viewModel.metadata?.['area-of-study'], '') as string}
                isErrored={!!fieldValidationErrors['area-of-study']}
                placeholder={
                    intl.formatHTMLMessage({
                        id: 'subjectArea',
                        defaultMessage: 'Subject Area',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('metadata', { ...verificationService.viewModel.metadata, 'area-of-study': newValue });
                        updateFieldValidationErrorsByFieldId('area-of-study', newValue, verificationService);
                    }
                }
            />

            <div className="sid-field__label sid-additional-info">
                <FormattedHTMLMessage id="planOfUse" defaultMessage="Plan of Use" />
            </div>

            <PlanOfUseComponent
                value={getSafe(() => viewModel.metadata?.['plan-of-use'], '') as string}
                isErrored={!!fieldValidationErrors['plan-of-use']}
                placeholder={
                    intl.formatHTMLMessage({
                        id: 'planOfUse',
                        defaultMessage: 'planOfUse',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('metadata', { ...verificationService.viewModel.metadata, 'plan-of-use': newValue });
                        updateFieldValidationErrorsByFieldId('plan-of-use', newValue, verificationService);
                    }
                }
            />

            <div className="sid-field__label sid-l-space-top-md">
                <FormattedHTMLMessage
                    id="step.personalInfo.licenseAgreement"
                    defaultMessage='All licenses provided under an Alteryx Academy program are governed by the Alteryx <a class="sid-link" target="_blank" href="https://www.alteryx.com/sites/default/files/2019-10/Alteryx-EULA-v-2019-03.pdf">End User License Agreement</a> and the <a class="sid-link" target="_blank" href="http://www.alteryx.com/privacy">Alteryx Privacy Policy</a>, as well as any additional terms of use outlined within each product, service, or site.'
                />
            </div>

            <div className="sid-field__label sid-l-space-top-md">
                <FormattedHTMLMessage
                    id="optInFutureCommunication"
                    defaultMessage="By submitting this form, I am opting in to receive future communications from SparkED education program."
                />
            </div>

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectTeacherPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        ref={(button: HTMLButtonElement) => setRef('submitButton', button)}
                    >
                        {
                            failedInstantMatch
                                ? <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                                : <FormattedHTMLMessage id="verifyMyTeacherStatus" defaultMessage="Verify My Teacher Status" />
                        }
                    </button>
                </div>
            </div>

            <FormFooterComponent verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="cantFindSchool"
                    defaultMessage="If your school is not in the list, please contact SheerID directly. Please send the <a class='sid-link' href='https://offers.sheerid.com/sheerid/add-school-request/?pid={pid}'>request form</a> after filling in the necessary items. If your school is added in the list, you will get the email notification from SheerID."
                    values={{
                        pid: programId,
                    }}
                />
                <div className="sid-footer sid-l-space-top-md">
                    <FormattedHTMLMessage
                        id="SparkEDFaq"
                        defaultMessage="Please visit our <a href=https://s3.us-east-2.amazonaws.com/alteryx.sparked/Documentation/SparkED+Getting+Started+FAQ.pdf target=“_blank”>SparkED: Getting Started FAQ</a> to learn about receiving and activating your Alteryx license key."
                    />
                </div>
            </FormFooterComponent>

            <PoweredByComponent verificationService={verificationService} />
        </div>
    );
};

export const StepTeacherPersonalInfoComponent = injectIntl(StepTeacherPersonalInfo);
